/* ============================================================
   VELOX — motion system (GSAP 3 + ScrollTrigger)
   One idea, carried through: elements ARRIVE UNSTABLE AND SETTLE.
   Loads before main.js so it can take ownership of the elements it
   animates (it strips their .reveal class; main.js keeps the rest).
   Safety: bails out cleanly (no hidden content) if GSAP is missing
   or the visitor prefers reduced motion.
   ============================================================ */
(function () {
  "use strict";
  const root = document.documentElement;
  const bail = () => root.classList.remove("m");

  if (!window.gsap || !window.ScrollTrigger ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches) { bail(); return; }

  try {
    gsap.registerPlugin(ScrollTrigger);
    const SETTLE = "back.out(1.6)";           // overshoot-and-settle = the brand ease
    const $  = (s, c) => (c || document).querySelector(s);
    const $$ = (s, c) => Array.from((c || document).querySelectorAll(s));
    const own = (el) => { if (el) el.classList.remove("reveal"); return el; };

    /* ---------- helpers ---------- */
    // Wrap each <br>-separated chunk of a heading in a mask so lines can rise into view.
    function maskLines(el) {
      if (!el || el.dataset.masked) return $$(".m-line__in", el);
      const chunks = el.innerHTML.split(/<br\s*\/?>/i);
      el.innerHTML = chunks.map((c) =>
        '<span class="m-line"><span class="m-line__in">' + c.trim() + "</span></span>").join("");
      el.dataset.masked = "1";
      return $$(".m-line__in", el);
    }
    // Count every integer inside a stat up from 0 (handles "13–50+").
    function countUp(el, delay) {
      const final = el.textContent;
      const parts = final.split(/(\d+)/);
      const nums = parts.map((p) => (/^\d+$/.test(p) ? { v: 0, to: +p } : null));
      if (!nums.some(Boolean)) return;
      const render = () => { el.textContent = parts.map((p, i) => nums[i] ? Math.round(nums[i].v) : p).join(""); };
      render();
      nums.forEach((n) => n && gsap.to(n, { v: n.to, duration: 1.1, delay: delay || 0, ease: "power2.out", onUpdate: render }));
    }
    // Draw the diagonal "instability cut" of a section from left to right.
    function drawCut(section, delay) {
      if (!section || !section.classList.contains("cut")) return;
      gsap.fromTo(section, { "--cut": "100%" }, { "--cut": "0%", duration: 1.1, delay: delay || 0, ease: "power3.inOut" });
    }

    /* ---------- 1. page intro ---------- */
    const hero = $(".hero");
    const intro = gsap.timeline({ defaults: { ease: "power3.out" } });

    if (hero) {
      const text   = $(".hero__text", hero);
      const kicker = $(".kicker", text);
      const title  = $(".hero__title", text);
      const lead   = $(".lead", text);
      const btns   = $$(".btn", text);
      const stats  = $$(".stat", text);
      const fig    = own($(".hero__figure", hero));
      const lines  = maskLines(title);
      const tilt   = $(".line2", title);

      gsap.set([kicker, lead, btns, stats, fig].flat().filter(Boolean), { autoAlpha: 0 });
      gsap.set(lines, { yPercent: 110 });
      if (tilt) gsap.set(tilt, { rotate: -5, transformOrigin: "0% 100%" });
      gsap.set(fig, { scale: 0.94, rotate: -4, transformOrigin: "50% 60%" });
      root.classList.remove("m");                       // CSS hide → inline hide, no flash

      intro
        .fromTo(kicker, { y: 12 }, { y: 0, autoAlpha: 1, duration: 0.5 }, 0.05)
        .to(lines, { yPercent: 0, duration: 0.9, stagger: 0.09, ease: "power4.out" }, 0.15)
        .to(tilt,  { rotate: 0, duration: 1.2, ease: SETTLE }, 0.32)
        .fromTo(lead, { y: 18 }, { y: 0, autoAlpha: 1, duration: 0.6 }, 0.55)
        .fromTo(btns, { y: 14 }, { y: 0, autoAlpha: 1, duration: 0.5, stagger: 0.08 }, 0.7)
        .fromTo(stats, { y: 12 }, { y: 0, autoAlpha: 1, duration: 0.5, stagger: 0.08 }, 0.85)
        .to(fig, { autoAlpha: 1, scale: 1, rotate: 0, duration: 1.3, ease: SETTLE }, 0.25);
      $$(".stat b", text).forEach((b, i) => countUp(b, 0.9 + i * 0.08));
      drawCut(hero, 0.2);
    } else {
      // Other pages: the opening h1 + its kicker + lead, same language, lighter.
      const h1 = own($("h1.display"));
      if (h1) {
        const section = h1.closest("section") || h1.parentElement;
        const kicker  = own(h1.previousElementSibling && h1.previousElementSibling.classList.contains("kicker") ? h1.previousElementSibling : null);
        const lead    = own(h1.nextElementSibling && h1.nextElementSibling.classList.contains("lead") ? h1.nextElementSibling : null);
        const lines   = maskLines(h1);
        const tilt    = $(".ice", h1);
        gsap.set([kicker, lead].filter(Boolean), { autoAlpha: 0 });
        gsap.set(lines, { yPercent: 110 });
        if (tilt) gsap.set(tilt, { rotate: -4, transformOrigin: "0% 100%", display: "inline-block" });
        root.classList.remove("m");
        intro
          .fromTo(kicker, { y: 12 }, { y: 0, autoAlpha: 1, duration: 0.5 }, 0.05)
          .to(lines, { yPercent: 0, duration: 0.85, stagger: 0.09, ease: "power4.out" }, 0.12)
          .to(tilt,  { rotate: 0, duration: 1.1, ease: SETTLE }, 0.28)
          .fromTo(lead, { y: 16 }, { y: 0, autoAlpha: 1, duration: 0.6 }, 0.45);
        drawCut(section, 0.15);
      } else {
        root.classList.remove("m");
      }
    }

    /* ---------- 2. headings settle in on scroll ---------- */
    $$(".display").forEach((h) => {
      if (h.closest(".hero") || h.tagName === "H1" || h.closest(".modal") || h.closest(".stagger")) return;
      const el = h.closest(".reveal") || h;          // take over the whole heading block
      if (el.dataset.settle) return;
      el.dataset.settle = "1";
      own(el);
      gsap.fromTo(el,
        { autoAlpha: 0, y: 26, rotate: -1.5, transformOrigin: "0% 100%" },
        { autoAlpha: 1, y: 0, rotate: 0, duration: 0.95, ease: SETTLE,
          scrollTrigger: { trigger: el, start: "top 86%", once: true } });
    });

    /* ---------- 3. diagonal cuts draw in as sections enter ---------- */
    $$("section.cut, .cut.section").forEach((s) => {
      if (s === hero || (intro && s.contains($("h1.display")))) return;
      gsap.set(s, { "--cut": "100%" });
      ScrollTrigger.create({ trigger: s, start: "top 75%", once: true, onEnter: () => drawCut(s) });
    });

    /* ---------- 4. stat counters outside the hero ---------- */
    $$(".stat b, .proof__v").forEach((b) => {
      if (hero && hero.contains(b)) return;
      ScrollTrigger.create({ trigger: b, start: "top 90%", once: true, onEnter: () => countUp(b) });
    });

    /* ---------- 5. ghost wordmark drifts against the scroll ---------- */
    $$(".hero, .waitband").forEach((s) => {
      gsap.to(s, { "--ghost-x": "-7vw", ease: "none",
        scrollTrigger: { trigger: s, start: "top bottom", end: "bottom top", scrub: 0.6 } });
    });

    /* ---------- 6. header compacts once you're reading ---------- */
    const header = $(".site-header");
    if (header) {
      ScrollTrigger.create({ start: 70, onUpdate: (st) => header.classList.toggle("is-scrolled", st.scroll() > 70) });
    }

    /* ---------- 7a. progression steps: card, ghost numeral, rail, node ---------- */
    $$(".steps").forEach((row) => {
      $$(".step", row).forEach((s, i) => {
        // NB: never fade the card itself — a transparent card exposes the grid's
        // line colour behind it as a grey slab (very visible when stacked on mobile).
        const tl = gsap.timeline({ delay: i * 0.13,
          scrollTrigger: { trigger: s, start: "top 88%", once: true } });
        tl.fromTo([$(".step__k", s), $("h3", s), $("p", s)],
                     { autoAlpha: 0, y: 18 },
                     { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.07, ease: "power3.out" }, 0)
          .fromTo($(".step__n", s), { autoAlpha: 0, x: 34 }, { autoAlpha: 1, x: 0, duration: 1, ease: "power3.out" }, 0.12)
          .fromTo($(".step__rail", s), { scaleX: 0 }, { scaleX: 1, duration: 0.65, ease: "power2.inOut" }, 0.3)
          .fromTo($(".step__node", s), { autoAlpha: 0, scale: 0 }, { autoAlpha: 1, scale: 1, duration: 0.45, ease: SETTLE }, 0.62);
      });
    });

    /* ---------- 7b. demand bars fill on entry ---------- */
    $$(".bar").forEach((b) => {
      const fill = $(".bar__fill", b);
      const v = parseFloat(b.dataset.v || "1");
      gsap.fromTo(fill, { scaleX: 0 }, { scaleX: v, duration: 1.05, ease: "power2.out",
        scrollTrigger: { trigger: b, start: "top 92%", once: true } });
    });

    /* ---------- 7c. filter pills ---------- */
    const bar = $("[data-filters]");
    if (bar) {
      const cards = $$("[data-cat]").filter((c) => !c.classList.contains("filter"));
      bar.addEventListener("click", (e) => {
        const btn = e.target.closest(".filter");
        if (!btn || btn.getAttribute("aria-pressed") === "true") return;
        const cat = btn.dataset.cat;
        $$(".filter", bar).forEach((b) => b.setAttribute("aria-pressed", String(b === btn)));
        // take the grid off the CSS stagger so it stops fighting these tweens
        if (cards[0] && cards[0].parentElement) cards[0].parentElement.classList.add("in");
        const show = cards.filter((c) => cat === "all" || c.dataset.cat === cat);
        const hide = cards.filter((c) => show.indexOf(c) === -1);
        const kids = (list) => list.reduce((a, c) => a.concat(Array.from(c.children)), []);
        // fade the contents, not the cards — a transparent card shows the grid's line colour
        gsap.to(kids(hide), { autoAlpha: 0, duration: 0.16, ease: "power2.in",
          onComplete: () => hide.forEach((c) => c.classList.add("is-filtered-out")) });
        show.forEach((c) => { c.classList.remove("is-filtered-out"); gsap.set(c, { clearProps: "all" }); });
        gsap.fromTo(kids(show), { autoAlpha: 0, y: 14 },
          { autoAlpha: 1, y: 0, duration: 0.45, stagger: 0.04, ease: SETTLE, delay: 0.12,
            onComplete: () => ScrollTrigger.refresh() });
      });
    }

    /* ---------- 7. grid cells: reveal the CONTENTS, never the cell ----------
       These grids paint their hairlines by showing a light background through
       2px gaps. A transparent cell therefore exposes that light wash as a grey
       slab, so motion.js takes the grids off the CSS stagger (which fades the
       cells) and animates what's inside each cell instead. */
    // .stagger grids are revealed by CSS (driven by main.js's IntersectionObserver).
    // Don't hand them to ScrollTrigger: if a trigger misses, content stays invisible.
    // Backstop: whatever happens, nothing stays hidden for more than 2.5s.
    setTimeout(() => $$(".stagger").forEach((g) => g.classList.add("in")), 2500);
    $$(".pcard, .range-card").forEach((card) => {
      if (card.closest(".stagger")) return;           // handled above
      own(card);
      gsap.fromTo(Array.from(card.children), { autoAlpha: 0, y: 20 },
        { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.07, ease: SETTLE,
          scrollTrigger: { trigger: card, start: "top 88%", once: true } });
    });
    /* ---------- 8. recompute trigger positions once images have loaded ----------
       Late-loading media shifts everything below it, which leaves ScrollTrigger
       holding stale start points — triggers already scrolled past then never
       fire and their content stays hidden. Refresh on load, and sweep up any
       reveal that still hasn't run while its element sits in view. */
    const sweep = () => {
      ScrollTrigger.refresh();
      ScrollTrigger.getAll().forEach((st) => {
        if (st.animation && !st.animation.progress() && st.scroll() >= st.start) {
          st.animation.progress(1);
        }
      });
    };
    window.addEventListener("load", () => { sweep(); setTimeout(sweep, 400); });
    if (document.readyState === "complete") { sweep(); setTimeout(sweep, 400); }

    /* Safety net: if a reveal's trigger is on screen but its animation still
       hasn't played, play it. IntersectionObserver is independent of scroll
       bookkeeping, so content can never be left stranded at opacity 0. */
    if ("IntersectionObserver" in window) {
      const net = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          ScrollTrigger.getAll().forEach((st) => {
            if (st.trigger !== en.target || !st.animation) return;
            if (st.vars && st.vars.scrub) return;          // scrubbed tweens follow scroll
            if (!st.animation.progress()) st.animation.play();
          });
          net.unobserve(en.target);
        });
      }, { threshold: 0.05 });
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger && st.animation && !(st.vars && st.vars.scrub)) net.observe(st.trigger);
      });
    }

  } catch (err) {
    // Never leave the page half-hidden: undo everything and fall back to CSS reveals.
    bail();
    try { gsap.set("[style]", { clearProps: "opacity,visibility,transform" }); } catch (_) {}
    if (window.console) console.warn("motion.js disabled:", err);
  }
})();
